import type { ActiveWalletDetailsFilter } from './WalletDetailsFilter.types';
import type { ReactElement } from 'react';

import { Box, Button, Flex, IconButton, Popover } from '@rango-dev/whale';
import { Sheet } from '@rango-dev/whale/lab';
import { EllipsisVertical } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import { useWalletDetails } from '../../../hooks/useWalletDetails';
import { useWindowDimensions } from '../../../hooks/useWindowDimensions';
import { resetFilters } from '../../../state/WalletDetailsSlice';

import { BlockchainFilter } from './BlockchainFilter';
import { ConnectedWalletFilter } from './ConnectedWalletFilter';
import { MoreFilter } from './MoreFilter';
import styles from './WalletDetailsFilter.module.css';
import { WalletDetailsFilterItem } from './WalletDetailsFilterItem';

const MORE_ICON_SIZE = 18;
const MORE_POPOVER_WIDTH = '250px';

export function WalletDetailsFilter(): ReactElement {
  const { isMobile } = useWindowDimensions();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { excludedBlockchains, excludedWallets } = useAppSelector((state) => state.walletDetails);

  const {
    toggleExcludedAllBlockchain,
    toggleExcludedAllWallet,
    connectedWallets,
    validBlockchains,
  } = useWalletDetails();

  const [activeFilter, setActiveFilter] = useState<ActiveWalletDetailsFilter>(null);

  const openStateFor = (filter: Exclude<ActiveWalletDetailsFilter, null>) => ({
    open: activeFilter === filter,
    onOpenChange: (open: boolean) => setActiveFilter(open ? filter : null),
  });

  const getWalletFilterTitle = () => {
    if (excludedWallets?.length === 0) {
      return t('All wallets');
    }

    const selectedWalletCount = connectedWallets?.length - excludedWallets?.length;

    if (isMobile) {
      return t(`{{selectedWalletCount}} Wallet${selectedWalletCount > 1 ? 's' : ''}`, {
        selectedWalletCount,
      });
    }

    return t('{{selectedWalletCount}}/{{connectedWalletCount}} Wallets', {
      selectedWalletCount,
      connectedWalletCount: connectedWallets.length,
    });
  };

  const blockchainFilterTitle = () => {
    if (excludedBlockchains?.length === 0) {
      return t('All chains');
    }

    const selectedBlockchainCount = validBlockchains?.length - excludedBlockchains?.length;

    if (isMobile) {
      return t(`{{selectedBlockchainCount}} Chain${selectedBlockchainCount > 1 ? 's' : ''}`, {
        selectedBlockchainCount,
      });
    }

    return t('{{selectedBlockchainCount}}/{{validBlockchainCount}} Chains', {
      selectedBlockchainCount,
      validBlockchainCount: validBlockchains.length,
    });
  };

  const showContentHeader = !isMobile;

  const renderToggleAllButton = (
    id: string,
    excludedItems: string[],
    handleToggleAll: () => void,
  ) => {
    if (isMobile) {
      return null;
    }
    return (
      <Button id={id} variant="soft" color="gray" size="3" onClick={() => handleToggleAll()}>
        {excludedItems?.length === 0 ? t('Deselect all') : t('Select all')}
      </Button>
    );
  };

  return (
    <>
      <Flex gap="3" align="center" width="100%">
        <Box flexGrow="1" minWidth="0">
          <WalletDetailsFilterItem
            id="wallet-details-modal-wallet-filter-btn"
            label={getWalletFilterTitle()}
            isFiltered={excludedWallets?.length > 0}
            popoverAlign="start"
            isMobile={isMobile}
            content={<ConnectedWalletFilter showHeader={showContentHeader} />}
            {...openStateFor('wallet')}
          />
        </Box>
        <Box flexGrow="1" minWidth="0">
          <WalletDetailsFilterItem
            id="wallet-details-modal-blockchain-filter-btn"
            label={blockchainFilterTitle()}
            isFiltered={excludedBlockchains?.length > 0}
            popoverAlign="end"
            isMobile={isMobile}
            content={<BlockchainFilter showHeader={showContentHeader} />}
            {...openStateFor('blockchain')}
          />
        </Box>

        {isMobile ? (
          <IconButton
            id="wallet-details-modal-more-filter-icon-btn"
            size="2"
            radius="full"
            variant="outline"
            color="gray"
            aria-label={t('More')}
            onClick={() => setActiveFilter('more')}>
            <EllipsisVertical size={MORE_ICON_SIZE} />
          </IconButton>
        ) : (
          <Popover.Root {...openStateFor('more')}>
            <Popover.Trigger>
              <IconButton
                id="wallet-details-modal-more-filter-icon-btn"
                size={{ initial: '2', sm: '3' }}
                radius="full"
                variant="outline"
                color="gray"
                aria-label={t('More')}>
                <EllipsisVertical size={MORE_ICON_SIZE} />
              </IconButton>
            </Popover.Trigger>
            <Popover.Content
              size="2"
              align="end"
              width={MORE_POPOVER_WIDTH}
              className={styles.popover}>
              <MoreFilter showHeader />
            </Popover.Content>
          </Popover.Root>
        )}
      </Flex>

      {isMobile && (
        <>
          <Sheet
            idPrefix="wallet-details-wallet-filter-sheet"
            title={t('Your Wallets')}
            footer={renderToggleAllButton(
              'wallet-details-wallet-filter-modal-toggle-all-btn',
              excludedWallets,
              toggleExcludedAllWallet,
            )}
            {...openStateFor('wallet')}>
            <ConnectedWalletFilter showHeader={false} />
          </Sheet>

          <Sheet
            idPrefix="wallet-details-blockchain-filter-sheet"
            title={t('Chains')}
            footer={renderToggleAllButton(
              'wallet-details-blockchain-filter-modal-toggle-all-btn',
              excludedBlockchains,
              toggleExcludedAllBlockchain,
            )}
            {...openStateFor('blockchain')}>
            <BlockchainFilter showHeader={false} />
          </Sheet>

          <Sheet
            idPrefix="wallet-details-more-filter-sheet"
            title={t('More')}
            footer={
              !isMobile ? (
                <Button
                  id="more-filter-reset-to-default-btn"
                  variant="soft"
                  color="gray"
                  size="3"
                  onClick={() => dispatch(resetFilters())}>
                  {t('Reset to default')}
                </Button>
              ) : null
            }
            {...openStateFor('more')}>
            <MoreFilter showHeader={false} />
          </Sheet>
        </>
      )}
    </>
  );
}
